import config from '../config';
import {GiftCardFormData} from '../giftCardShop/screens/schema';
import {AirtablePromotionItem} from '../models/airtable';
import {AppRoute} from '../models/AppRoute';
import {ZenGiftCardsPaymentInstrument} from '../models/mfl';
import {GiftCardRecipientData, Shop, StoreByDistance, Voucher} from '../models/middleware';
import * as RootNavigation from '../navigation/RootNavigation';
import type {RootStore} from '../stores/rootStore';

import {toLocalISOString} from './date';

// Land on the liquor store home after leaving the gift card store.
//
// Switching store remounts the entire NavigationContainer, so navigationRef
// briefly reports isReady() === false. A dispatch while it is still the old
// container is dropped or applied to the wrong tree. GIFT_CARDS_PARTICIPATING_STORES
// is TargetStoreType.All, so it survives back into the liquor store.
//
// The way IN does not use this. Web rebuilds state from window.location, so
// navigateToGiftCardsShop replaces the URL with /gift-cards before selectStore
// returns. A reset here would race the remount and flash the menu.
//
// On the way out: once the container is ready again, resetRoot the whole tree
// to ROOT > MAIN > [home]. resetRoot (not navigate) so MAIN holds ONLY home.
const HOME_NAV_MAX_ATTEMPTS = 20;
const HOME_NAV_RETRY_MS = 50;

const navigateToStoreHome = (rootStore: RootStore) => {
  const target = rootStore.shopSelectionStore.homeAppRoute;
  const dispatchWhenReady = (attempt = 0) => {
    if (RootNavigation.navigationRef.isReady()) {
      RootNavigation.navigationRef.resetRoot({
        index: 0,
        routes: [
          {
            name: AppRoute.ROOT,
            state: {
              index: 0,
              routes: [{name: AppRoute.MAIN, state: {index: 0, routes: [{name: target}]}}],
            },
          },
        ],
      });
      return;
    }
    if (attempt < HOME_NAV_MAX_ATTEMPTS) {
      setTimeout(() => dispatchWhenReady(attempt + 1), HOME_NAV_RETRY_MS);
    }
  };
  dispatchWhenReady();
};

export type ZenGiftCardsPaymentInstrumentWithChargedAmount = ZenGiftCardsPaymentInstrument & {
  chargedAmount: number; // cents
};

const makePlaceholderShop = (storeNumber: string): Shop => ({
  id: 'placeholder',
  name: 'placeholder',
  phone: 'placeholder',
  store_number: storeNumber,
  loyalty_enabled: true,
  address: {
    lines: ['placeholder'],
    postal_code: 'placeholder',
    administrative_area: 'placeholder',
    locality: 'placeholder',
  },
  delivery_available: true,
  pickup_available: true,
  distance: 0,
  openToday: null,
  shop_role: 'placeholder',
});

/**
 * Navigate to gift cards shop - accepts rootStore as parameter to avoid circular dependency
 */
export const navigateToGiftCardsShop = async (rootStore: RootStore) => {
  const selectedStoreId = rootStore.shopSelectionStore.selectedStore?.store_number;
  // Guard: only save to liquor storage if current store is NOT already a gift card store
  if (selectedStoreId && selectedStoreId !== config.GIFT_CARD_STORE) {
    await rootStore.shopSelectionStore.setLiquorShopSalesforceIdToStorage(selectedStoreId);
  }
  // Web rebuilds navigation state from window.location when the container
  // remounts. replaceState does not notify the still-mounted tree (no popstate),
  // so the menu stays put until the splash unmounts it. The new tree then opens
  // on /gift-cards. A resetRoot after selectStore races that remount: it either
  // hits the old container or snaps the menu a second time.
  pointBrowserAtGiftCardShop();
  const placeholderGiftCardShop: Shop = makePlaceholderShop(config.GIFT_CARD_STORE);
  await rootStore.shopSelectionStore.selectStore(placeholderGiftCardShop);
};

const GIFT_CARD_SHOP_PATH = '/gift-cards';

const pointBrowserAtGiftCardShop = () => {
  if (typeof window === 'undefined' || typeof window.history?.replaceState !== 'function') return;
  if (window.location.pathname === GIFT_CARD_SHOP_PATH) return;
  window.history.replaceState(window.history.state, '', GIFT_CARD_SHOP_PATH);
};

/**
 * Pure function to create gift card recipient data from form values
 */
export const makeGiftCardRecipientData = (values: GiftCardFormData): GiftCardRecipientData => ({
  note: values.note,
  recipient_name: values.recipientName,
  recipient_email_address: values.recipientEmail,
  recipient_phone_number: values.recipientPhoneNumber,
  gift_email_date: toLocalISOString(values.deliveryDate),
});

/**
 * Navigate to liquor shop - accepts rootStore as parameter to avoid circular dependency
 */
export const navigateToLiquorShop = async (
  navigation: {navigate: (route: string, params?: unknown) => void},
  rootStore: RootStore,
) => {
  // Try to use previously selected store first (more reliable)
  // Guard: ensure previouslySelectedStore is not a gift card store
  const previousSnapshot = rootStore.shopSelectionStore.previouslySelectedStore;
  if (previousSnapshot && previousSnapshot.store.store_number !== config.GIFT_CARD_STORE) {
    try {
      await rootStore.shopSelectionStore.selectPreviousStore();
      // Verify restoration succeeded — if still on gift card store, fall through
      if (!rootStore.shopSelectionStore.isSelectedStoreGiftCardStore) {
        // The navigator's emergent reset-to-home doesn't fire from the `All`-typed
        // Participating Stores screen (it survives the store switch), so drive it.
        navigateToStoreHome(rootStore);
        return;
      }
    } catch (_error) {
      // If previous store selection fails, fall through to other options
    }
  }

  let localStorageLiquorStore =
    await rootStore.shopSelectionStore.loadLiquorShopSalesforceIdFromLocalStorage();

  // TODO: hack for old buggy dev versions
  if (localStorageLiquorStore === config.GIFT_CARD_STORE) {
    localStorageLiquorStore = null;
    await rootStore.shopSelectionStore.clearLiquorShopSalesforceIdFromStorage();
  }

  if (localStorageLiquorStore) {
    const placeholderLiquorShop: Shop = makePlaceholderShop(localStorageLiquorStore);
    try {
      await rootStore.shopSelectionStore.selectStore(placeholderLiquorShop);
      // Same reason as the previous-store path: drive the landing on home.
      navigateToStoreHome(rootStore);
    } catch (_error) {
      // Store not found (404) or other error - clear invalid store and navigate to selection
      await rootStore.shopSelectionStore.clearLiquorShopSalesforceIdFromStorage();
      navigation.navigate(AppRoute.ROOT_STORE_SELECTION_FLOW, {
        screen: AppRoute.MY_STORE_SUBURB_SELECTION,
      });
    }
  } else {
    // React Navigation v7: MY_STORE_SUBURB_SELECTION is in ROOT_STORE_SELECTION_FLOW
    navigation.navigate(AppRoute.ROOT_STORE_SELECTION_FLOW, {
      screen: AppRoute.MY_STORE_SUBURB_SELECTION,
    });
  }
};

/**
 * Navigate to previous store - accepts rootStore as parameter to avoid circular dependency
 */
export const navigateToPreviousStore = (rootStore: RootStore) =>
  rootStore.shopSelectionStore.selectPreviousStore();

const PARTICIPATING_STORES_PATH = '/gift-cards-participating-stores';

const participatingStoresOwnHosts = (): string[] =>
  [
    config.HOST_DOMAIN,
    config.HOST_DOMAIN ? `www.${config.HOST_DOMAIN}` : undefined,
    config.HOST_DOMAIN ? `beta.${config.HOST_DOMAIN}` : undefined,
    config.WHITE_LABEL_ID ? `${config.WHITE_LABEL_ID}-brand-dev.lmg-bw.au` : undefined,
  ].filter((host): host is string => Boolean(host));

const pathIsParticipatingStores = (pathname: string): boolean =>
  pathname === PARTICIPATING_STORES_PATH || pathname === `${PARTICIPATING_STORES_PATH}/`;

/** Own-domain (or relative) participating-stores href from the gift-card shop blurb. */
export const isGiftCardParticipatingStoresHref = (url: string): boolean => {
  if (!url) return false;
  if (url.startsWith('/')) {
    return pathIsParticipatingStores(url.split('?')[0]);
  }
  try {
    const parsed = new URL(url);
    return (
      pathIsParticipatingStores(parsed.pathname) &&
      participatingStoresOwnHosts().includes(parsed.hostname)
    );
  } catch {
    return false;
  }
};

/**
 * Gift-card shop stack does not mount GIFT_CARDS_PARTICIPATING_STORES. MAIN does
 * (`TargetStoreType.All`) when the shopper reached the shop through the app.
 * Walk ancestors and navigate on the navigator that actually owns the route so
 * GIFT_CARDS_MAIN stays in the back stack (form intact). Do not use openPageLink
 * here — its reset fallback is what DIS-1604 was avoiding.
 *
 * Return false = we handled it (suppress markdown Linking.openURL).
 * Return true = no ancestor mounts the route (or the href is foreign) — browser.
 */
type BlurbLinkNavigation = {
  navigate: (name: typeof AppRoute.GIFT_CARDS_PARTICIPATING_STORES) => void;
  getParent?: () => BlurbLinkNavigation | undefined;
  getState?: () => {routeNames?: ReadonlyArray<string>} | undefined;
};

export const handleGiftCardShopBlurbLinkPress = (
  navigation: BlurbLinkNavigation,
  url: string,
): boolean => {
  if (!isGiftCardParticipatingStoresHref(url)) {
    return true;
  }
  let nav: BlurbLinkNavigation | undefined = navigation;
  while (nav) {
    if (nav.getState?.()?.routeNames?.includes(AppRoute.GIFT_CARDS_PARTICIPATING_STORES)) {
      nav.navigate(AppRoute.GIFT_CARDS_PARTICIPATING_STORES);
      return false;
    }
    nav = nav.getParent?.();
  }
  return true;
};

export const isGiftCardsPromotion = (promotion: AirtablePromotionItem) =>
  promotion.activationUrlName === config.GIFT_CARDS_PROMOTION_ID;

export const getGiftCardRemainingBalance = (giftCard: Voucher) =>
  parseFloat(giftCard.vouchervalue) - parseFloat(giftCard.valueused);

// :/
export const isGiftCardProduct = (product: {name: string; brand_name?: string | null}) =>
  product.name.toLowerCase().includes('gift card') &&
  product.brand_name === 'Liquor Marketing Group';

/** Physical / in-store gift-card channel (Salesforce In_Store_Gift_Cards__c). */
export const hasInStoreGiftCardChannel = (shop: Shop): boolean => shop.instoreGiftCards === 'Live';

/**
 * Online gift-card shopping channel.
 *
 * DIS-1460: uses static ecommerce `pickup_available` (Online_Delivery__c ===
 * 'Live'), NOT suburb-scoped `delivery_available` (MFL locality / ~5km).
 * Note: `pickup_available` is misnamed — it means ecommerce-capable, not
 * “pickup fulfillment only”.
 */
export const hasOnlineGiftCardChannel = (shop: Shop): boolean => !!shop.pickup_available;

/**
 * Pick up chip — same bit as FIND YOUR STORE / HomePage StoreNameCard
 * (`shopSelectionStore.setCanPickup(store.pickup_available)`).
 */
export const hasGiftCardPickupCapability = (shop: Shop): boolean => !!shop.pickup_available;

/**
 * Delivery chip — same bit as FIND YOUR STORE / HomePage StoreNameCard
 * (`shopSelectionStore.setCanDeliver(store.delivery_available)`), i.e. MFL
 * suburb-scoped delivery from `/members/search` for the suburb just queried.
 */
export const hasGiftCardDeliveryCapability = (shop: Shop): boolean => !!shop.delivery_available;

// A store qualifies for the Check Store Location list if it can sell gift
// cards via EITHER channel:
//   - Physical: instoreGiftCards === 'Live'
//   - Online/ecommerce: pickup_available === true
// instoreGiftCards is optional and may be undefined / null / '' / 'In Progress'.
// A missing or non-Live value must not exclude an otherwise online-capable
// store from the list.
export const filterGiftCardStores = (shops: Shop[]): Shop[] =>
  shops.filter(shop => hasInStoreGiftCardChannel(shop) || hasOnlineGiftCardChannel(shop));

/** Closest → furthest. Missing distance sorts last. Does not mutate input. */
export const sortGiftCardStoresByDistance = (shops: Shop[]): Shop[] =>
  [...shops].sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));

/**
 * DIS-1501-B. Adapt a `StoreByDistance` (from GET /members/stores/:tenant) into
 * the `Shop` the gift-card card + filter/sort already consume, so the flag-ON
 * path reuses the existing UI unchanged. `openToday` is intentionally null —
 * the card derives open-hours from `regular_hours` via formatOpenTodayText.
 * `id`/`shop_role` are unused by the card; stubbed to satisfy the Shop type.
 */
export const storeByDistanceToShop = (item: StoreByDistance): Shop => ({
  name: item.tradingName,
  phone: item.phone,
  store_number: item.storeNumber,
  id: item.storeNumber,
  shop_role: '',
  address: {
    lines: [item.addressLine1, item.addressLine2].filter(Boolean),
    locality: item.suburb,
    administrative_area: item.state,
    postal_code: item.postcode,
  },
  distance: item.distance,
  pickup_available: item.pickupAvailable,
  delivery_available: item.deliveryAvailable ?? false,
  loyalty_enabled: item.loyaltyEnabled,
  regular_hours: item.regularHours,
  instoreGiftCards: item.instoreGiftCards,
  openToday: null,
});
